import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, Minimize2, Paperclip, Plus } from "lucide-react";
import { CSSProperties, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router";

import { StatusPill, type StatusPillTone } from "nazare-ui/components";
import { cn } from "nazare-ui/utils";

import { useDocumentTitle } from "../../../../use-document-title";
import { useSecondaryHeader } from "../../../../secondary-header-context";
import { ScrollShadow, TABLE_HEADER_HEIGHT } from "../../../../scroll-shadow";
import { InfiniteScrollSentinel, SkeletonRow } from "../../../../skeleton-row";
import { useInfiniteRows } from "../../../../use-infinite-rows";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useSimulatedLoad } from "../../../../use-simulated-load";
import styles from "./runs.module.css";

type RunStatus = "queued" | "running" | "completed" | "failed";

type WorkflowRun = {
  /** `wrun_<ULID>` — same id shape as the reference dashboard. */
  id: string;
  status: RunStatus;
  workflowName: string;
  /** Muted secondary line under the workflow name — the mailbox, company,
   * channel, or "system" that the run relates to/was triggered by. */
  secondary: string;
  /** Hardcoded display string, not a `Date` — Workflow scripts forbid
   * non-deterministic calls like `new Date()`/`Date.now()`. */
  createdAt: string;
  artefactCount: number;
  /** `null` renders as "—" (queued/still-running runs have no duration yet). */
  duration: string | null;
};

const STATUS_LABEL: Record<RunStatus, string> = {
  queued: "Queued",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
};

const STATUS_TONE: Record<RunStatus, StatusPillTone> = {
  queued: "neutral",
  running: "info",
  completed: "success",
  failed: "error",
};

/** Static mock runs, newest first — there is no live workflow-orchestration
 * backend in `platform` yet. Shaped after the reference `/admin/workflows`
 * runs table but extended to a full page's worth of history (~40 rows)
 * spanning several days, independent of the abbreviated dashboard-card
 * dataset in `../runs-table.tsx` (deliberately not imported/shared). */
const RUNS: WorkflowRun[] = [
  {
    id: "wrun_01KX3P4R7VQH2N6TC8ZMD9F3AK",
    status: "running",
    workflowName: "ingestGmailInbox",
    secondary: "dealflow@phoenixcourt.vc",
    createdAt: "07/07/2026, 22:41:07",
    artefactCount: 0,
    duration: null,
  },
  {
    id: "wrun_01KX3P3H5TN8Q4V2RC6MD7F9BJ",
    status: "running",
    workflowName: "classifyInboundEmail",
    secondary: "dealflow@phoenixcourt.vc",
    createdAt: "07/07/2026, 22:39:52",
    artefactCount: 0,
    duration: null,
  },
  {
    id: "wrun_01KX3P2C9RF4T7N2VD8HMC3S6Q",
    status: "queued",
    workflowName: "sendNotification",
    secondary: "partners@phoenixcourt.vc",
    createdAt: "07/07/2026, 22:37:15",
    artefactCount: 0,
    duration: null,
  },
  {
    id: "wrun_01KX3P1B6QN3T8H4VC7RD2F5MK",
    status: "completed",
    workflowName: "identifyCompanyUpdate",
    secondary: "component-labs",
    createdAt: "07/07/2026, 22:18:04",
    artefactCount: 3,
    duration: "2m 41s",
  },
  {
    id: "wrun_01KX3NZQ8HF2N6T4VD9RC3S7BJ",
    status: "failed",
    workflowName: "syncAffinityCRM",
    secondary: "system",
    createdAt: "07/07/2026, 22:05:29",
    artefactCount: 0,
    duration: "38s",
  },
  {
    id: "wrun_01KX3NYK5TR7Q3N9HD2VC8F4SM",
    status: "completed",
    workflowName: "syncCalendarEvents",
    secondary: "thomas@phoenixcourt.vc",
    createdAt: "07/07/2026, 21:58:12",
    artefactCount: 1,
    duration: "18s",
  },
  {
    id: "wrun_01KX3NXD3QN6T8H2VC9RD4F7BK",
    status: "completed",
    workflowName: "refreshPortfolioMetrics",
    secondary: "system",
    createdAt: "07/07/2026, 21:40:37",
    artefactCount: 5,
    duration: "4m 06s",
  },
  {
    id: "wrun_01KX3NW86RF3T9N4HD7VC2S5MQ",
    status: "running",
    workflowName: "enrichCompanyProfile",
    secondary: "nebula-robotics",
    createdAt: "07/07/2026, 21:36:02",
    artefactCount: 0,
    duration: null,
  },
  {
    id: "wrun_01KX3NV49HT6Q2N8RD3VC7F5SK",
    status: "completed",
    workflowName: "scoreDealSentiment",
    secondary: "orbital-health",
    createdAt: "07/07/2026, 21:12:45",
    artefactCount: 2,
    duration: "56s",
  },
  {
    id: "wrun_01KX3NTS7RQ4T8H3VD2FC6N9BM",
    status: "completed",
    workflowName: "extractPitchDeckMetadata",
    secondary: "atlas-materials",
    createdAt: "07/07/2026, 20:51:33",
    artefactCount: 4,
    duration: "1m 41s",
  },
  {
    id: "wrun_01KX3NSK2VN8Q6H4TD9RC3F7SJ",
    status: "completed",
    workflowName: "ingestGmailInbox",
    secondary: "dealflow@phoenixcourt.vc",
    createdAt: "07/07/2026, 20:47:19",
    artefactCount: 4,
    duration: "3m 22s",
  },
  {
    id: "wrun_01KX3NRB4TQ7N3H8VD6RC2F9SM",
    status: "queued",
    workflowName: "notifyPartnerSlack",
    secondary: "#deal-flow",
    createdAt: "07/07/2026, 20:44:58",
    artefactCount: 0,
    duration: null,
  },
  {
    id: "wrun_01KX3NQ92HR6T4N8VC3RD9F5BK",
    status: "completed",
    workflowName: "flagComplianceRisk",
    secondary: "vantage-biotech",
    createdAt: "07/07/2026, 20:22:41",
    artefactCount: 1,
    duration: "1m 09s",
  },
  {
    id: "wrun_01KX3NP74QT3N9H2VD8RC6F4SJ",
    status: "completed",
    workflowName: "classifyInboundEmail",
    secondary: "ir@phoenixcourt.vc",
    createdAt: "07/07/2026, 20:03:16",
    artefactCount: 1,
    duration: "14s",
  },
  {
    id: "wrun_01KX3NN63RF8T2N6HD4VC9S7BQ",
    status: "completed",
    workflowName: "generateBoardPack",
    secondary: "lumen-grid",
    createdAt: "07/07/2026, 19:47:52",
    artefactCount: 7,
    duration: "6m 18s",
  },
  {
    id: "wrun_01KX3NM58HN4Q7T3VD2RC6F9SK",
    status: "completed",
    workflowName: "syncAffinityCRM",
    secondary: "system",
    createdAt: "07/07/2026, 19:15:03",
    artefactCount: 0,
    duration: "45s",
  },
  {
    id: "wrun_01KWZKB63TQ9N4H8RD2VC7F5SM",
    status: "completed",
    workflowName: "generateWeeklyDigest",
    secondary: "system",
    createdAt: "07/07/2026, 08:05:11",
    artefactCount: 6,
    duration: "5m 51s",
  },
  {
    id: "wrun_01KWZK92H7TQ3N6F4VD8RC5S2M",
    status: "completed",
    workflowName: "publishPortfolioDigest",
    secondary: "ir@phoenixcourt.vc",
    createdAt: "07/07/2026, 07:52:38",
    artefactCount: 2,
    duration: "1m 27s",
  },
  {
    id: "wrun_01KWZK7Q3RN6T8H2VD9FC4S7BJ",
    status: "failed",
    workflowName: "syncZoomTranscripts",
    secondary: "system",
    createdAt: "07/07/2026, 06:41:22",
    artefactCount: 0,
    duration: "22s",
  },
  {
    id: "wrun_01KWYKA11QB4V3JRFCSP643N8B",
    status: "completed",
    workflowName: "archiveStaleThreads",
    secondary: "system",
    createdAt: "06/07/2026, 23:59:47",
    artefactCount: 12,
    duration: "1m 03s",
  },
  {
    id: "wrun_01KWYK83H4TN7Q2V9RD6C3F8SM",
    status: "completed",
    workflowName: "enrichFounderProfile",
    secondary: "fathom-analytics",
    createdAt: "06/07/2026, 23:31:09",
    artefactCount: 3,
    duration: "2m 06s",
  },
  {
    id: "wrun_01KWYK6R9NQ3T8H4VD2FC7S5BJ",
    status: "completed",
    workflowName: "syncHubspotPipeline",
    secondary: "system",
    createdAt: "06/07/2026, 22:48:31",
    artefactCount: 0,
    duration: "1m 52s",
  },
  {
    id: "wrun_01KWYK4Q6TR8N4H3VD9RC2F7SK",
    status: "completed",
    workflowName: "computeReturnAttribution",
    secondary: "system",
    createdAt: "06/07/2026, 21:56:14",
    artefactCount: 1,
    duration: "3m 47s",
  },
  {
    id: "wrun_01KWYK2N8HQ4T7N3VD6RC9F2SM",
    status: "completed",
    workflowName: "ingestGmailInbox",
    secondary: "dealflow@phoenixcourt.vc",
    createdAt: "06/07/2026, 21:14:52",
    artefactCount: 2,
    duration: "2m 58s",
  },
  {
    id: "wrun_01KWYK1H4RN6T2H8VD3RC7F9BJ",
    status: "completed",
    workflowName: "deduplicateContacts",
    secondary: "system",
    createdAt: "06/07/2026, 20:37:20",
    artefactCount: 0,
    duration: "48s",
  },
  {
    id: "wrun_01KWYJZ73QT8N4H2VD9RC6F5SK",
    status: "completed",
    workflowName: "syncDocuSignEnvelopes",
    secondary: "cascade-security",
    createdAt: "06/07/2026, 20:02:44",
    artefactCount: 5,
    duration: "1m 33s",
  },
  {
    id: "wrun_01KWYJXQ2HN7T3H9VD4RC8F6SM",
    status: "completed",
    workflowName: "notifyPartnerSlack",
    secondary: "#portfolio-ops",
    createdAt: "06/07/2026, 19:44:11",
    artefactCount: 0,
    duration: "9s",
  },
  {
    id: "wrun_01KWY7G4S9NQ4T6H3VDRC8B5MJ",
    status: "completed",
    workflowName: "backupKnowledgeBase",
    secondary: "system",
    createdAt: "06/07/2026, 19:30:02",
    artefactCount: 1,
    duration: "9m 14s",
  },
  {
    id: "wrun_01KWY7B39HQ7T3N4VD2RC6F8SK",
    status: "completed",
    workflowName: "refreshMarketComps",
    secondary: "pulsegrid-energy",
    createdAt: "06/07/2026, 18:52:27",
    artefactCount: 3,
    duration: "2m 11s",
  },
  {
    id: "wrun_01KWY79R6TN4Q8H3VD9RC2F5SJ",
    status: "completed",
    workflowName: "classifyInboundEmail",
    secondary: "ops@phoenixcourt.vc",
    createdAt: "06/07/2026, 17:26:58",
    artefactCount: 1,
    duration: "11s",
  },
  {
    id: "wrun_01KWY77H4QN8T3H2VD6RC9F4SM",
    status: "completed",
    workflowName: "syncInvestorUpdates",
    secondary: "ir@phoenixcourt.vc",
    createdAt: "06/07/2026, 16:48:33",
    artefactCount: 4,
    duration: "1m 19s",
  },
  {
    id: "wrun_01KWY6E9RTQ3N6H4VD8FC2S7BK",
    status: "completed",
    workflowName: "ingestTwitterMentions",
    secondary: "system",
    createdAt: "06/07/2026, 15:33:47",
    artefactCount: 9,
    duration: "3m 05s",
  },
  {
    id: "wrun_01KWY6D8R4TN3Q7H9VFC2S6BMJ",
    status: "completed",
    workflowName: "reindexSearchCorpus",
    secondary: "system",
    createdAt: "06/07/2026, 14:02:39",
    artefactCount: 0,
    duration: "42s",
  },
  {
    id: "wrun_01KWY6B72QH4T8N3VD9RC6F5SK",
    status: "completed",
    workflowName: "computeCoInvestorGraph",
    secondary: "system",
    createdAt: "06/07/2026, 12:19:05",
    artefactCount: 1,
    duration: "4m 52s",
  },
  {
    id: "wrun_01KWY6996TN7Q3H2VD8RC4F9SM",
    status: "failed",
    workflowName: "syncGoogleDriveFolders",
    secondary: "harborlight-fintech",
    createdAt: "06/07/2026, 11:05:18",
    artefactCount: 0,
    duration: "1m 02s",
  },
  {
    id: "wrun_01KWY5C4Q7NT8F4H2VDRC9S6BJ",
    status: "completed",
    workflowName: "pruneExpiredArtefacts",
    secondary: "system",
    createdAt: "05/07/2026, 07:10:22",
    artefactCount: 8,
    duration: "1m 27s",
  },
  {
    id: "wrun_01KWY5A82RT4Q6N9HD3VC7F5SK",
    status: "completed",
    workflowName: "generateDealMemo",
    secondary: "meridian-foundry",
    createdAt: "05/07/2026, 06:38:51",
    artefactCount: 5,
    duration: "2m 34s",
  },
  {
    id: "wrun_01KWY588H4QN7T3H2VD9RC6F4SM",
    status: "completed",
    workflowName: "rotateApiCredentials",
    secondary: "system",
    createdAt: "05/07/2026, 05:12:07",
    artefactCount: 0,
    duration: "6s",
  },
  {
    id: "wrun_01KWY566T3RN8Q4H9VD2FC7S5BK",
    status: "completed",
    workflowName: "validateDataRoomAccess",
    secondary: "driftwood-logistics",
    createdAt: "05/07/2026, 04:47:36",
    artefactCount: 2,
    duration: "51s",
  },
  {
    id: "wrun_01KWY544Q7TN3H8V2RD9C6F4SM",
    status: "completed",
    workflowName: "syncSlackChannels",
    secondary: "#eng-alerts",
    createdAt: "05/07/2026, 03:58:14",
    artefactCount: 0,
    duration: "13s",
  },
  {
    id: "wrun_01KWY522H8QT4N7H3VD6RC9F5SK",
    status: "completed",
    workflowName: "archiveInactiveThreads",
    secondary: "system",
    createdAt: "05/07/2026, 02:31:49",
    artefactCount: 14,
    duration: "1m 11s",
  },
];

/**
 * This system logs far more than 41 runs — that was just the hand-authored
 * "recent history" sample above. `TOTAL_RUN_COUNT` is the real, accurate
 * total this page claims (shown in the header), and `getRun(index)` below
 * extends the hardcoded `RUNS` array out to that count with deterministic
 * synthetic rows for infinite scroll, rather than pretending the dataset
 * stops where the hand-written sample happens to stop.
 */
const TOTAL_RUN_COUNT = 1247;

/** Deterministic 32-bit string hash (FNV-1a) + seeded PRNG — same technique
 * as `run-detail.tsx`'s `deriveRunDetail`, so the SAME index always
 * produces the SAME synthetic row (no `Math.random()`/`Date.now()`). */
function hashSeed(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const ULID_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

function syntheticUlid(rand: () => number): string {
  let id = "";
  for (let i = 0; i < 26; i += 1) {
    id += ULID_ALPHABET[Math.floor(rand() * ULID_ALPHABET.length)];
  }
  return `wrun_${id}`;
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** Formats a Date the same "DD/MM/YYYY, HH:MM:SS" shape every hardcoded
 * `RUNS.createdAt` above already uses. */
function formatCreatedAt(date: Date): string {
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}, ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

// Fixed baseline — the OLDEST hardcoded row above — synthetic rows continue
// backward in time from here, never from "now" (this component never calls
// `Date.now()`/`new Date()` with no arguments).
const OLDEST_HARDCODED_RUN_MS = new Date(2026, 6, 5, 2, 31, 49).getTime();

const SECONDARY_POOL = Array.from(new Set(RUNS.map((run) => run.secondary)));

/** A run beyond the hand-authored `RUNS` history — deterministic per
 * `index`, so scrolling to the same position always shows the same row. */
function generateSyntheticRun(index: number): WorkflowRun {
  const rand = mulberry32(hashSeed(`run-${index}`));
  const workflowName =
    WORKFLOW_FILTER_VALUES[Math.floor(rand() * WORKFLOW_FILTER_VALUES.length)];
  const secondary = SECONDARY_POOL[Math.floor(rand() * SECONDARY_POOL.length)];

  // Older synthetic history reads as fully settled — almost everything is
  // completed by now, with the occasional failure; nothing this far back is
  // still queued/running.
  const statusRoll = rand();
  const status: RunStatus = statusRoll < 0.9 ? "completed" : "failed";

  // ~45 minutes further back per row beyond the hardcoded sample, ±10min of
  // jitter so consecutive gaps don't look mechanically identical — jitter
  // is deliberately kept smaller than the fixed step so `minutesBack` stays
  // strictly increasing with `index` no matter how the jitter rolls (was
  // `indexDelta * randomFactor`, which could easily make a LATER row's
  // total smaller than an EARLIER row's, rendering the "Created" column
  // out of order — a real audit log is always sorted).
  const minutesBack =
    (index - RUNS.length + 1) * 45 + Math.floor(rand() * 21) - 10;
  const createdAt = formatCreatedAt(
    new Date(OLDEST_HARDCODED_RUN_MS - minutesBack * 60_000),
  );

  const durationSeconds = 5 + Math.floor(rand() * 360);
  const duration =
    durationSeconds >= 60
      ? `${Math.floor(durationSeconds / 60)}m ${String(durationSeconds % 60).padStart(2, "0")}s`
      : `${durationSeconds}s`;

  return {
    id: syntheticUlid(rand),
    status,
    workflowName,
    secondary,
    createdAt,
    artefactCount: status === "failed" ? 0 : Math.floor(rand() * 12),
    duration,
  };
}

function getRun(index: number): WorkflowRun {
  return index < RUNS.length ? RUNS[index] : generateSyntheticRun(index);
}

/** Distinct workflow names present in `RUNS`, in order of first appearance —
 * the "All workflows" dropdown's option list. Independent of the dashboard
 * card's own equivalent list in `../runs-table.tsx` (deliberately not
 * imported/shared — see this file's own `RUNS` doc comment above). */
const WORKFLOW_FILTER_VALUES: string[] = Array.from(
  new Set(RUNS.map((run) => run.workflowName)),
);

const STATUS_FILTER_VALUES: RunStatus[] = [
  "queued",
  "running",
  "completed",
  "failed",
];

type FilterOption<T extends string> = {
  /** `null` = the reset ("All …") option. */
  value: T | null;
  label: string;
};

type FilterDropdownProps<T extends string> = {
  options: FilterOption<T>[];
  value: T | null;
  onChange: (value: T | null) => void;
  "aria-label": string;
};

/**
 * Small filter dropdown for the "Runs" secondary-header bar — mirrors the
 * Popover.Root/Trigger/Portal/Content structure (and .content/.list/.option/
 * .optionActive/.checkIcon styling convention) used by the nav rail's
 * `<ThemeToggle>`/`<NotificationBell>` popovers, generic over the filter's
 * value type so the same markup drives both the workflow and status
 * dropdowns below. An independent implementation of the same pattern used by
 * the dashboard's compact Runs card (`../runs-table.tsx`) — not imported
 * from there, per this route's own filter state. Picking an option both
 * updates the filter and closes the popover.
 */
function FilterDropdown<T extends string>({
  options,
  value,
  onChange,
  "aria-label": ariaLabel,
}: FilterDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const activeLabel =
    options.find((option) => option.value === value)?.label ??
    options[0]?.label ??
    "";

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={styles.filterButton}
          aria-label={ariaLabel}
        >
          {activeLabel}
          <ChevronDown className={styles.filterIcon} aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          className={styles.dropdownContent}
        >
          <ul className={styles.dropdownList} role="listbox">
            {options.map((option) => {
              const isActive = option.value === value;
              return (
                <li key={option.value ?? "__all__"}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={cn(
                      styles.dropdownOption,
                      isActive && styles.dropdownOptionActive,
                    )}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <span className={styles.dropdownOptionLabel}>
                      {option.label}
                    </span>
                    {isActive && (
                      <Check className={styles.checkIcon} aria-hidden="true" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const WORKFLOW_FILTER_OPTIONS: FilterOption<string>[] = [
  { value: null, label: "All workflows" },
  ...WORKFLOW_FILTER_VALUES.map((workflowName) => ({
    value: workflowName,
    label: workflowName,
  })),
];

const STATUS_FILTER_OPTIONS: FilterOption<RunStatus>[] = [
  { value: null, label: "All statuses" },
  ...STATUS_FILTER_VALUES.map((status) => ({
    value: status,
    label: STATUS_LABEL[status],
  })),
];

/**
 * Full-page `/workflows/runs` route: the "maximised" table view of the
 * dashboard's `WorkflowRunsTable` (`../runs-table.tsx`) card, opened via a
 * grow transition from that card (shared `viewTransitionName`). The shell's
 * breadcrumbs already read "Workflows > Runs" for this route, so this page
 * renders no title of its own — just the table card, filling the outlet and
 * scrolling internally so the shell chrome stays fixed. Filter controls live
 * in the shell's secondary header row (see `secondary-header-context.tsx`),
 * not inline on the page — the "All workflows"/"All statuses" buttons there
 * are real dropdown menus that filter the visible rows client-side
 * (AND-combined), and each row is a real, whole-row link to its run's detail
 * page. All data is static mock content — there is no live
 * workflow-orchestration backend in `platform` yet.
 */
export function WorkflowRunsPage() {
  useDocumentTitle("Runs");

  const navigate = useNavigate();
  const goToDashboard = () => {
    if (!document.startViewTransition) {
      navigate("/workflows");
      return;
    }
    document.startViewTransition(() => flushSync(() => navigate("/workflows")));
  };

  // Initial page-mount loading state — distinct from `isLoadingMore` below,
  // which only covers the infinite-scroll "load more" case further down the
  // list. This one covers the very first render, before any real rows (not
  // even the first `initialCount` page) have ever appeared.
  const isInitialLoading = useSimulatedLoad();

  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const { loadedCount, isLoadingMore, hasMore, sentinelRef } = useInfiniteRows({
    totalCount: TOTAL_RUN_COUNT,
    initialCount: RUNS.length,
    pageSize: 30,
    scrollRootRef: tableWrapRef,
  });
  const [workflowFilter, setWorkflowFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<RunStatus | null>(null);

  // Filtering applies to whatever's been loaded so far, same as any
  // client-side filter over an infinite-scrolled list — it doesn't fetch
  // ahead to find matches beyond `loadedCount`.
  const loadedRuns = useMemo(
    () => Array.from({ length: loadedCount }, (_, index) => getRun(index)),
    [loadedCount],
  );

  const visibleRuns = useMemo(
    () =>
      loadedRuns.filter((run) => {
        if (workflowFilter && run.workflowName !== workflowFilter) return false;
        if (statusFilter && run.status !== statusFilter) return false;
        return true;
      }),
    [loadedRuns, workflowFilter, statusFilter],
  );

  const isFiltered = workflowFilter !== null || statusFilter !== null;

  // Referentially stable only when [workflowFilter, statusFilter] actually
  // change — required by useSecondaryHeader's own contract (see its doc
  // comment): a fresh JSX literal on every render would re-fire that hook's
  // effect every render, which re-renders the provider (and this page) in
  // an infinite loop.
  const secondaryHeaderContent = useMemo(
    () => (
      <div className={styles.filters}>
        <FilterDropdown
          options={WORKFLOW_FILTER_OPTIONS}
          value={workflowFilter}
          onChange={setWorkflowFilter}
          aria-label="Filter by workflow"
        />
        <FilterDropdown
          options={STATUS_FILTER_OPTIONS}
          value={statusFilter}
          onChange={setStatusFilter}
          aria-label="Filter by status"
        />
        <span className={styles.spacer} />
        <button type="button" className={styles.startRunButton}>
          <Plus size={14} aria-hidden />
          Start new run
        </button>
      </div>
    ),
    [workflowFilter, statusFilter],
  );
  useSecondaryHeader(secondaryHeaderContent);

  return (
    <div className={styles.page}>
      <section
        className={styles.card}
        style={{ viewTransitionName: "wf-card-runs" } as unknown as CSSProperties}
      >
        <header className={styles.header}>
          <span className={styles.count}>
            {isFiltered
              ? `${visibleRuns.length} of ${loadedCount} loaded`
              : `${TOTAL_RUN_COUNT} runs`}
          </span>
          <button
            type="button"
            className={styles.minimiseButton}
            onClick={goToDashboard}
            aria-label="Minimise to dashboard"
            title="Minimise to dashboard"
          >
            <Minimize2 className={styles.minimiseIcon} aria-hidden="true" />
          </button>
        </header>

        <div className={styles.scrollShadowWrapper}>
        <div ref={tableWrapRef} className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th} scope="col">
                  Status
                </th>
                <th className={styles.th} scope="col">
                  Workflow
                </th>
                <th className={styles.th} scope="col">
                  Run ID
                </th>
                <th className={styles.th} scope="col">
                  Created
                </th>
                <th className={styles.th} scope="col">
                  Artefacts
                </th>
                <th className={`${styles.th} ${styles.thRight}`} scope="col">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody>
              {isInitialLoading ? (
                Array.from({ length: 8 }, (_, index) => (
                  <SkeletonRow key={`initial-skeleton-${index}`} columns={6} />
                ))
              ) : (
                <>
                  {visibleRuns.map((run) => (
                    <tr key={run.id} className={styles.row}>
                      <td className={styles.td}>
                        {/* Stretched-link pattern: a `<tr>` can't be wrapped in
                           an `<a>` (invalid HTML), so this empty, visually-
                           hidden `<Link>` — rendered as the first child of the
                           row's first `<td>` — is absolutely positioned to
                           cover the entire row (`.rowLink` = `position:
                           absolute; inset: 0`, `.row` = `position: relative`).
                           A real `<a href>` for the whole row, so cmd/ctrl/
                           middle-click all work, not just an onClick handler. */}
                        <Link
                          to={`/workflows/runs/${run.id}`}
                          viewTransition
                          className={styles.rowLink}
                          aria-label={`View run ${run.id}`}
                        />
                        <StatusPill tone={STATUS_TONE[run.status]}>
                          {STATUS_LABEL[run.status]}
                        </StatusPill>
                      </td>
                      <td className={styles.td}>
                        <div className={styles.workflowName}>{run.workflowName}</div>
                        <div className={styles.workflowSecondary}>{run.secondary}</div>
                      </td>
                      <td className={`${styles.td} ${styles.runId}`}>{run.id}</td>
                      <td className={`${styles.td} ${styles.created}`}>
                        {run.createdAt}
                      </td>
                      <td className={styles.td}>
                        <span className={styles.artefacts}>
                          <Paperclip className={styles.artefactsIcon} aria-hidden="true" />
                          {run.artefactCount}
                        </span>
                      </td>
                      <td
                        className={`${styles.td} ${styles.duration} ${
                          run.duration ? "" : styles.durationEmpty
                        }`}
                      >
                        {run.duration ?? "—"}
                      </td>
                    </tr>
                  ))}
                  {hasMore && (
                    <InfiniteScrollSentinel sentinelRef={sentinelRef} columns={6} />
                  )}
                  {isLoadingMore &&
                    Array.from({ length: 3 }, (_, index) => (
                      <SkeletonRow key={`skeleton-${index}`} columns={6} />
                    ))}
                </>
              )}
            </tbody>
          </table>
        </div>
        <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
        </div>
      </section>
    </div>
  );
}
